import React, { useEffect, useState } from 'react'
import { Menu, Modal, Popover } from '@mantine/core'
import { ChevronRight } from 'tabler-icons-react'
import {
	getEmbeddedFrameSource,
	openEmbeddedPageInNewTab,
} from '../pages/embeddedPageUtils'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

// Keep this read-only viewer's data and state separate from check-in actions.
const CheckInEmbeddedPages = ({ checkInId, children }) => {
	const [pages, setPages] = useState([])
	const [submenuOpened, setSubmenuOpened] = useState(false)
	const [activePage, setActivePage] = useState(null)

	useEffect(() => {
		if (!checkInId) return undefined
		const controller = new AbortController()

		fetch(`${BASE_URL}/api/myexperience/${encodeURIComponent(checkInId)}`, {
			signal: controller.signal,
		})
			.then(async (response) => {
				if (!response.ok) throw new Error('Experience lookup failed')
				return response.json()
			})
			.then((data) => {
				if (controller.signal.aborted) return
				setPages(
					Array.isArray(data.experience?.embeddedPages)
						? data.experience.embeddedPages.filter(
								(page) => page && getEmbeddedFrameSource(page.source),
							)
						: [],
				)
			})
			.catch(() => {
				// A viewer lookup failure must never interrupt the check-in workflow.
			})

		return () => controller.abort()
	}, [checkInId])

	const openPage = (page) => {
		setSubmenuOpened(false)
		const frameSource = getEmbeddedFrameSource(page.source)
		if (openEmbeddedPageInNewTab(frameSource)) return
		setActivePage({ ...page, frameSource })
	}

	const menuItem =
		pages.length > 0 ? (
			<Popover
				opened={submenuOpened}
				onChange={setSubmenuOpened}
				position='right-start'
				offset={4}
				shadow='md'
				withinPortal={false}
				withRoles={false}
			>
				<Popover.Target>
					<Menu.Item
						closeMenuOnClick={false}
						onClick={() => setSubmenuOpened((opened) => !opened)}
						rightSection={<ChevronRight size={16} aria-hidden='true' />}
						aria-haspopup='menu'
						aria-expanded={submenuOpened}
					>
						View embedded page(s)
					</Menu.Item>
				</Popover.Target>
				<Popover.Dropdown p={4} role='menu' aria-label='Embedded pages'>
					{pages.map((page, index) => (
						<Menu.Item
							key={`${page.label}-${index}`}
							onClick={() => openPage(page)}
						>
							{page.label}
						</Menu.Item>
					))}
				</Popover.Dropdown>
			</Popover>
		) : null

	return (
		<>
			{children(menuItem)}
			<Modal
				opened={Boolean(activePage)}
				onClose={() => setActivePage(null)}
				title={activePage?.label}
				size='1100px'
				centered
				styles={{ body: { padding: 0 } }}
			>
				{activePage && (
					<iframe
						{...activePage.frameSource}
						title={activePage.label}
						style={{
							display: 'block',
							width: '100%',
							height: 'min(740px, calc(100dvh - 120px))',
							border: 0,
						}}
						sandbox={
							activePage.frameSource.src
								? 'allow-scripts allow-same-origin allow-forms allow-presentation'
								: 'allow-scripts allow-forms allow-presentation'
						}
						allow='clipboard-write; encrypted-media; fullscreen; picture-in-picture'
						allowFullScreen
					/>
				)}
			</Modal>
		</>
	)
}

export default CheckInEmbeddedPages
